/**
 * Concord cryptographic primitives (CORD-02, Appendix A).
 *
 * HKDF-SHA256 and SHA-256 are implemented over WebCrypto so the module has no
 * native-code dependency and works in every modern browser. NIP-44 v2 and
 * secp256k1 key operations come from nostr-tools.
 */

import { getPublicKey } from "nostr-tools";
import * as nip44 from "nostr-tools/nip44";

const encoder = new TextEncoder();

export function utf8(s: string): Uint8Array {
  return encoder.encode(s);
}

export function bytesToHex(b: Uint8Array): string {
  let out = "";
  for (let i = 0; i < b.length; i++) out += b[i].toString(16).padStart(2, "0");
  return out;
}

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0 || /[^0-9a-f]/i.test(hex)) {
    throw new Error("invalid hex");
  }
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

export function concatBytes(...arrays: Uint8Array[]): Uint8Array {
  const len = arrays.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(len);
  let off = 0;
  for (const a of arrays) {
    out.set(a, off);
    off += a.length;
  }
  return out;
}

export function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
}

/** Unpadded base64url (RFC 4648 §5) — CORD-02 §8's wire encoding. */
export function bytesToB64url(b: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < b.length; i++) bin += String.fromCharCode(b[i]);
  return btoa(bin).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

export function b64urlToBytes(s: string): Uint8Array {
  const b64 = s.replaceAll("-", "+").replaceAll("_", "/");
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  const digest = await crypto.subtle.digest("SHA-256", data as BufferSource);
  return new Uint8Array(digest);
}

async function hmacSha256(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key as BufferSource,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", cryptoKey, data as BufferSource);
  return new Uint8Array(sig);
}

/**
 * HKDF-SHA256 (RFC 5869), general form. Exposed at this arity so the test
 * vectors can exercise it; Concord uses the specialization below.
 */
export async function hkdfSha256(
  ikm: Uint8Array,
  salt: Uint8Array,
  info: Uint8Array,
  length: number,
): Promise<Uint8Array> {
  const prk = await hmacSha256(salt.length ? salt : new Uint8Array(32), ikm);
  const out = new Uint8Array(length);
  let t = new Uint8Array(0);
  for (let i = 0; i * 32 < length; i++) {
    t = await hmacSha256(prk, concatBytes(t, info, new Uint8Array([i + 1])));
    out.set(t.subarray(0, Math.min(32, length - i * 32)), i * 32);
  }
  return out;
}

/**
 * HKDF-SHA256 with a zero-length salt (represented as HashLen zeros) and a
 * 32-byte output length — exactly the shape CORD-02 A.1 uses.
 */
export async function hkdf(ikm: Uint8Array, info: Uint8Array): Promise<Uint8Array> {
  return hkdfSha256(ikm, new Uint8Array(32), info, 32);
}

function u64be(n: number): Uint8Array {
  const out = new Uint8Array(8);
  new DataView(out.buffer).setBigUint64(0, BigInt(n), false);
  return out;
}

const SECP256K1_ORDER = BigInt(
  "0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141",
);

function isValidScalar(b: Uint8Array): boolean {
  if (b.length !== 32) return false;
  const n = BigInt("0x" + bytesToHex(b));
  return n > 0n && n < SECP256K1_ORDER;
}

export interface GroupKey {
  /** The derived secret key — signs the plane's wraps. */
  sk: Uint8Array;
  /** x-only pubkey hex — the plane's stream address. */
  pk: string;
  /** NIP-44 self-ECDH conversation key — encrypts the wraps. */
  convKey: Uint8Array;
}

/**
 * CORD-02 A.2 group_key: HKDF the secret under a label/id/epoch, normalize the
 * seed into a secp256k1 keypair, and derive the NIP-44 self-ECDH conversation
 * key used to encrypt that plane's wraps.
 */
export async function groupKey(
  label: string,
  secret: Uint8Array,
  id: Uint8Array,
  epoch?: number,
): Promise<GroupKey> {
  if (id.length !== 32) throw new Error("group_key id must be 32 bytes");
  const base = concatBytes(
    utf8(label),
    new Uint8Array([0]),
    id,
    ...(epoch === undefined ? [] : [u64be(epoch)]),
  );
  // A.3 scalar_normalize: retry with an appended counter byte, starting at 0.
  let counter = -1;
  for (;;) {
    const info =
      counter < 0 ? base : concatBytes(base, new Uint8Array([counter & 0xff]));
    const seed = await hkdf(secret, info);
    if (isValidScalar(seed)) {
      const pk = getPublicKey(seed); // x-only hex
      const convKey = nip44.v2.utils.getConversationKey(seed, pk);
      return { sk: seed, pk, convKey };
    }
    counter++;
  }
}

/** CORD-02 §1 / A.4: the self-certifying community identity. */
export async function communityId(
  ownerXonlyHex: string,
  ownerSalt: Uint8Array,
): Promise<string> {
  const digest = await sha256(
    concatBytes(utf8("concord/community"), hexToBytes(ownerXonlyHex), ownerSalt),
  );
  return bytesToHex(digest);
}

const ZERO32 = new Uint8Array(32);

/** The Control Plane read key (every member holds it via the community_root). */
export async function controlReadKey(
  communityRootHex: string,
  communityIdHex: string,
  epoch: number,
): Promise<GroupKey> {
  return groupKey(
    "concord/control",
    hexToBytes(communityRootHex),
    hexToBytes(communityIdHex),
    epoch,
  );
}

/** The Control Plane signer — only staff (control_root holders) can derive it. */
export async function controlSignerKey(
  controlRootHex: string,
  communityIdHex: string,
  epoch: number,
): Promise<GroupKey> {
  return groupKey(
    "concord/control-signer",
    hexToBytes(controlRootHex),
    hexToBytes(communityIdHex),
    epoch,
  );
}

/** The Guestbook plane key (member-writable). */
export async function guestbookKey(
  communityRootHex: string,
  communityIdHex: string,
  epoch: number,
): Promise<GroupKey> {
  return groupKey(
    "concord/guestbook",
    hexToBytes(communityRootHex),
    hexToBytes(communityIdHex),
    epoch,
  );
}

/**
 * A channel's chat plane key. For public channels the secret is the
 * community_root and the epoch is the root epoch; for private channels it's
 * the channel's own key and epoch (CORD-03 §1).
 */
export async function channelKey(
  secretHex: string,
  channelIdHex: string,
  epoch: number,
): Promise<GroupKey> {
  return groupKey(
    "concord/channel",
    hexToBytes(secretHex),
    hexToBytes(channelIdHex),
    epoch,
  );
}

/** Deterministic Control Plane entity coordinates (CORD-02 A.6). */
export async function grantLocator(
  communityIdHex: string,
  memberHex: string,
): Promise<string> {
  const key = await groupKey(
    "concord/grant",
    hexToBytes(communityIdHex),
    hexToBytes(memberHex),
  );
  return key.pk;
}

export async function banlistLocator(communityIdHex: string): Promise<string> {
  const key = await groupKey("concord/banlist", hexToBytes(communityIdHex), ZERO32);
  return key.pk;
}

/** CORD-02 A.5 epoch-key commitment (used by rekeys). */
export async function epochKeyCommitment(
  prevEpoch: number,
  prevKey: Uint8Array,
): Promise<string> {
  const digest = await sha256(
    concatBytes(utf8("concord/epoch-key-commitment"), u64be(prevEpoch), prevKey),
  );
  return bytesToHex(digest);
}
